import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Home, Film, Scissors, Shield, Star, MoreHorizontal } from "lucide-react";

/**
 * Mobile-only bottom tab bar. 5 tabs: Home · Games · Clips · Admin · More.
 * `More` opens the hamburger drawer (fires a custom event the Sidebar listens to).
 * For non-admin users, the Admin slot is replaced with Highlights so the bar is always 5 items.
 * Automatically hides on viewports wider than 900px via CSS media query.
 */
const BottomTabBar = () => {
  const { user } = useAuth();
  const location = useLocation();

  const canAdmin = ["ysv_admin", "league_admin", "pro_league_admin", "org_admin", "coach"].includes(user?.role);

  const fourthTab = canAdmin
    ? { path: "/admin", label: "Admin", icon: Shield }
    : { path: "/highlights", label: "Highlights", icon: Star };

  const tabs = [
    { path: "/home", label: "Home", icon: Home },
    { path: "/games", label: "Games", icon: Film },
    { path: "/clips", label: "Clips", icon: Scissors },
    fourthTab,
  ];

  const openDrawer = () => {
    window.dispatchEvent(new CustomEvent("ysv:open-nav-drawer"));
  };

  return (
    <>
      <nav data-testid="bottom-tabbar" className="ysv-bottom-tabbar" aria-label="Primary mobile navigation">
        {tabs.map(t => {
          const active = location.pathname.startsWith(t.path);
          return (
            <NavLink
              key={t.path}
              to={t.path}
              data-testid={`bottom-tab-${t.label.toLowerCase()}`}
              className="ysv-bottom-tab"
              style={{
                color: active ? "var(--cyan)" : "var(--text-secondary)",
                background: active ? "rgba(0,229,255,0.06)" : "transparent",
              }}
            >
              <t.icon size={20} strokeWidth={active ? 2.3 : 1.8} />
              <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, letterSpacing: 0.2 }}>{t.label}</span>
            </NavLink>
          );
        })}

        <button
          data-testid="bottom-tab-more"
          type="button"
          onClick={openDrawer}
          className="ysv-bottom-tab"
          style={{ color: "var(--text-secondary)", background: "transparent", border: "none" }}
          aria-label="Open more menu"
        >
          <MoreHorizontal size={22} strokeWidth={1.8} />
          <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: 0.2 }}>More</span>
        </button>
      </nav>

      <style>{`
        .ysv-bottom-tabbar {
          display: none;
        }
        .ysv-bottom-tab {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          padding: 8px 0;
          text-decoration: none;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          transition: background 0.12s ease, color 0.12s ease;
          border-radius: 10px;
          margin: 4px 2px;
          min-height: 52px;
        }
        .ysv-bottom-tab:hover {
          background: rgba(255,255,255,0.03);
        }
        @media (max-width: 900px) {
          .ysv-bottom-tabbar {
            display: flex;
            position: fixed;
            left: 0; right: 0;
            bottom: 0;
            height: calc(64px + env(safe-area-inset-bottom, 0px));
            padding-bottom: env(safe-area-inset-bottom, 0px);
            background: #0d0d10;
            border-top: 1px solid var(--dark-border);
            z-index: 70;
            box-shadow: 0 -8px 24px rgba(0,0,0,0.5);
          }
          body.ysv-with-bottom-tabbar main[data-testid="app-main"] {
            padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
          }
        }
      `}</style>
    </>
  );
};

export default BottomTabBar;
